import React, { useEffect, useState } from "react";
import axios from "axios";
import { Authorization } from "../Context/Authorization";
import { exportRangeWiseReport } from "../utils/exportToExcel";
import "./reporttable.css";

/* ──────────────────────────────────────────────────────────
   Utility helpers
────────────────────────────────────────────────────────── */
const groupBy = (arr, key) =>
  arr.reduce((acc, item) => {
    const g = item[key] ?? "Unknown";
    if (!acc[g]) acc[g] = [];
    acc[g].push(item);
    return acc;
  }, {});

const aggregateStats = (ranges) => {
  const loanTypeTotals = {};

  ranges.forEach((r) => {
    const types = r.loanTypeTotals || {};

    Object.entries(types).forEach(([type, val]) => {
      if (!loanTypeTotals[type]) {
        loanTypeTotals[type] = { loan: 0, recovered: 0 };
      }

      loanTypeTotals[type].loan += val.loan || 0;
      loanTypeTotals[type].recovered += val.recovered || 0;
    });
  });

  return {
    totalVFCs: ranges.reduce((s, r) => s + (r.totalVFCs || 0), 0),
    totalMembers: ranges.reduce((s, r) => s + (r.totalMembers || 0), 0),
    totalLoans: ranges.reduce((s, r) => s + (r.totalLoans || 0), 0),
    loanTypeTotals,
  };
};

const fmt = (val) => {
  if (val === null || val === undefined || val === "") return "—";
  if (typeof val === "number") return val.toLocaleString("en-IN");
  return val;
};

/* ──────────────────────────────────────────────────────────
   Small reusable sub-components
────────────────────────────────────────────────────────── */
const StatChip = ({ label, value }) => (
  <div className="rwr-stat-chip">
    <span className="rwr-stat-label">{label}</span>
    <span className="rwr-stat-value">{fmt(value)}</span>
  </div>
);



const StatsRow = ({ stats, allLoanTypes = [], showVfcCount = false, jfmcLabel = "VFC" }) => (
  <div className="rwr-stats-row">
    {showVfcCount && (
      <StatChip
        label={`${jfmcLabel}s`}
        value={stats.totalVFCs}
      />
    )}
    <StatChip label="Members" value={stats.totalMembers} />
    <StatChip label="Active Loans" value={stats.totalLoans} />

    {(allLoanTypes || []).map((type) => {
      const val = stats.loanTypeTotals?.[type] || {};

      return (
        <React.Fragment key={type}>
          <StatChip label={`${type} ₹`} value={val.loan || 0} />
          <StatChip label={`${type} Recovered ₹`} value={val.recovered || 0} />
        </React.Fragment>
      );
    })}
  </div>
);

/* ──────────────────────────────────────────────────────────
   VFC Mini Table (shown when a Range row is expanded)
────────────────────────────────────────────────────────── */
const VfcTable = ({ vfcList, jfmcLabel }) => {
  if (!vfcList || vfcList.length === 0) {
    return <div className="rwr-empty">No {jfmcLabel} data available for this range.</div>;
  }
  const allLoanTypes = [
    ...new Set(
      vfcList.flatMap(v =>
        (v.yearlyReport || []).flatMap(y =>
          Object.entries(y.loanTypes || {})
            .filter(([_, val]) => val.loan > 0 || val.recovered > 0)
            .map(([k]) => k)
        )
      )
    )
  ];

  return (
    <div className="rwr-vfc-table-wrapper">
      <table className="rwr-vfc-table">
        <thead>
          <tr>
            <th>{jfmcLabel} Name</th>
            <th>Members</th>
            <th>Year</th>
            <th>Active Loans</th>

            {allLoanTypes.map(type => (
              <React.Fragment key={type}>
                <th>{type} Loan (₹)</th>
                <th>{type} Recovered (₹)</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {vfcList.map((vfc) => {
            const years = Array.isArray(vfc.yearlyReport) ? vfc.yearlyReport : [];

            if (years.length === 0) {
              return (
                <tr key={vfc.vfcName}>
                  <td className="rwr-vfc-name-cell">{vfc.vfcName}</td>
                  <td>{fmt(vfc.totalMembers)}</td>
                  <td colSpan="6" className="rwr-no-data">No yearly data</td>
                </tr>
              );
            }

            return years.map((y, idx) => (
              <tr
                key={`${vfc.vfcName}-${y.year}-${idx}`}
                className={idx === 0 ? "rwr-vfc-start" : ""}
              >
                {idx === 0 && (
                  <>
                    <td rowSpan={years.length} className="rwr-vfc-name-cell">
                      {vfc.vfcName}
                    </td>
                    <td rowSpan={years.length}>
                      {fmt(years[0]?.totalMembers)}
                    </td>
                  </>
                )}
                <td className="rwr-year-cell">{y.year}</td>
                <td>{fmt(y.totalActiveLoans)}</td>
                {allLoanTypes.map(type => {
                  const data = y.loanTypes?.[type] || {};
                  return (
                    <React.Fragment key={type}>
                      <td>{fmt(data.loan)}</td>
                      <td>{fmt(data.recovered)}</td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ));
          })}

          <tr className="rwr-separator">
            <td colSpan="8"></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────
   Main Component
────────────────────────────────────────────────────────── */
const RangeWiseReportTable = () => {
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCircles, setExpandedCircles] = useState({});
  const [expandedDivisions, setExpandedDivisions] = useState({});
  const [expandedRanges, setExpandedRanges] = useState({});
  const [jfmcTypes, setJfmcTypes] = useState([]);
  const [activeJfmc, setActiveJfmc] = useState(null);

  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    if (!activeJfmc) return;

    clearOldCaches();   // ✅ correct place

    const cacheKey = `rangeReport_${activeJfmc._id}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      setReport(JSON.parse(cached));
      setLoading(false);
    } else {
      fetchReport();
    }

  }, [activeJfmc]);


  useEffect(() => {
    fetchJfmcTypes();
    clearOldCaches()
  }, []);

  const fetchJfmcTypes = async () => {
    try {
      const res = await axios.get(`${Api}/api/jfmc-types`, Authorization());
      const data = res.data || [];

      setJfmcTypes(data);

      // ✅ auto select first (like review dashboard)
      if (data.length > 0) {
        setActiveJfmc(data[0]);
      }
    } catch (err) {
      console.error("Error fetching jfmc types", err);
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${Api}/api/all/range-wise`,
        {
          ...Authorization(),
          params: {
            jfmcTypeId: activeJfmc?._id
          }
        }
      );

      const data = response.data || [];

      setReport(data);

      // ✅ Save cache for this JFMC only
      const cacheKey = `rangeReport_${activeJfmc._id}`;
      localStorage.setItem(cacheKey, JSON.stringify(data));

    } catch (error) {
      console.error("Error fetching range-wise report", error);
    } finally {
      setLoading(false);
    }
  };

  const clearOldCaches = () => {
    if (!activeJfmc?._id) return; // ✅ prevent crash

    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("rangeReport_") && !key.includes(activeJfmc._id)) {
        localStorage.removeItem(key);
      }
    });
  };

  const toggleCircle = (name) =>
    setExpandedCircles((prev) => ({ ...prev, [name]: !prev[name] }));

  const toggleDivision = (key) =>
    setExpandedDivisions((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleRange = (key) =>
    setExpandedRanges((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ── expand / collapse all ── */
  const expandAll = () => {
    const circles = {}, divisions = {}, ranges = {};
    circleGroups && Object.entries(circleGroups).forEach(([c, cRanges]) => {
      circles[c] = true;
      const divs = groupBy(cRanges, "division");
      Object.entries(divs).forEach(([d, dRanges]) => {
        divisions[`${c}__${d}`] = true;
        dRanges.forEach((r) => { ranges[`${c}__${d}__${r.range}`] = true; });
      });
    });
    setExpandedCircles(circles);
    setExpandedDivisions(divisions);
    setExpandedRanges(ranges);
  };

  const collapseAll = () => {
    setExpandedCircles({});
    setExpandedDivisions({});
    setExpandedRanges({});
  };

  if (loading) {
    return (
      <div className="rwr-loading">
        <div className="rwr-spinner" />
        <span>Loading report…</span>
      </div>
    );
  }
  const jfmcLabel = activeJfmc?.jfmcTypeName || "VFC";

  const filteredReport = report.filter(r =>
    (r.totalVFCs > 0) ||
    (r.vfcYearWiseReport && r.vfcYearWiseReport.length > 0)
  );

  const sortedReport = [...filteredReport].sort((a, b) =>
    String(a.circle || "").localeCompare(String(b.circle || ""))
  );

  const allLoanTypes = [
    ...new Set(
      sortedReport.flatMap(r =>
        Object.entries(r.loanTypeTotals || {})
          .filter(([_, v]) => (v.loan > 0 || v.recovered > 0))
          .map(([k]) => k)
      )
    )
  ];
  const circleGroups = groupBy(sortedReport, "circle");
  const totalCircles = Object.keys(circleGroups).length;

  return (
    <div className="rwr-container">

      {/* ── Page Header ── */}
      <div className="rwr-page-header">
        <div>
          <h5 className="rwr-title">
            <i className="mdi mdi-file-chart" />
            Range-Wise Report
          </h5>
          <p className="rwr-subtitle">
            {totalCircles} Circles — Click any row to expand details
          </p>
        </div>
        <div className="jfmc-switch">
          {jfmcTypes.sort((a, b) =>
            String(a.jfmcTypeName || "").localeCompare(
              String(b.jfmcTypeName || ""),
              undefined,
              { sensitivity: "base" }
            )
          ).map((j) => (
            <button
              key={j._id}
              className={`btn ${activeJfmc?._id === j._id
                ? "btn-success"
                : "btn-outline-secondary"
                }`}
              onClick={() => setActiveJfmc(j)}
            >
              {j.jfmcTypeName}
            </button>
          ))}
        </div>
        <div className="rwr-header-actions">
          <button className="rwr-action-btn" onClick={expandAll} title="Expand All">
            <i className="mdi mdi-unfold-more-horizontal" /> Expand All
          </button>
          <button className="rwr-action-btn" onClick={collapseAll} title="Collapse All">
            <i className="mdi mdi-unfold-less-horizontal" /> Collapse All
          </button>
          <button
            className="btn btn-success btn-sm"
            onClick={() => exportRangeWiseReport(report)}
          >
            <i className="mdi mdi-microsoft-excel" /> Export Excel
          </button>
        </div>
      </div>

      {/* ── Legend ── */}
      <div className="rwr-legend">
        <span className="rwr-legend-item rwr-legend-circle">Circle</span>
        <i className="mdi mdi-chevron-right rwr-legend-arrow" />
        <span className="rwr-legend-item rwr-legend-division">Division</span>
        <i className="mdi mdi-chevron-right rwr-legend-arrow" />
        <span className="rwr-legend-item rwr-legend-range">Range</span>
        <i className="mdi mdi-chevron-right rwr-legend-arrow" />
        <span className="rwr-legend-item rwr-legend-vfc">JFMC Data</span>
      </div>
      {/* ── Circle Level ── */}
      <div className="rwr-report-scroll">
        <div className="rwr-report-content">
          {/* ── Circle Level ── */}
          {Object.entries(circleGroups).map(([circleName, circleRanges]) => {
            const circleStats = aggregateStats(circleRanges);
            const isCircleOpen = !!expandedCircles[circleName];
            const divisionGroups = groupBy(circleRanges, "division");
            const divCount = Object.keys(divisionGroups).length;

            return (
              <div key={circleName} className="rwr-circle-block">

                {/* Circle Header */}
                <div
                  className="rwr-circle-header"
                  onClick={() => toggleCircle(circleName)}
                  role="button"
                  aria-expanded={isCircleOpen}
                >
                  <div className="rwr-row-left">
                    <span className="rwr-level-badge rwr-badge-circle">Circle</span>
                    <span className="rwr-row-name">{circleName}</span>
                  </div>

                  <div className="rwr-row-right">
                    <span className="rwr-count-pill">
                      <i className="mdi mdi-sitemap" /> {divCount} Div
                    </span>
                    <i
                      className={`mdi mdi-chevron-down rwr-chevron ${isCircleOpen ? "rotated" : ""
                        }`}
                    />
                  </div>

                  <div className="rwr-row-mid">
                    <StatsRow
                      stats={circleStats}
                      allLoanTypes={allLoanTypes}
                      jfmcLabel={jfmcLabel}
                      showVfcCount
                    />
                  </div>
                </div>

                {/* Division Level */}
                {isCircleOpen && (
                  <div className="rwr-children rwr-circle-children">
                    {Object.entries(divisionGroups).map(([divisionName, divRanges]) => {
                      const divisionStats = aggregateStats(divRanges);
                      const divKey = `${circleName}__${divisionName}`;
                      const isDivOpen = !!expandedDivisions[divKey];
                      const rangeCount = divRanges.length;

                      return (
                        <div key={divisionName} className="rwr-division-block">

                          {/* Division Header */}
                          <div
                            className="rwr-division-header"
                            onClick={() => toggleDivision(divKey)}
                            role="button"
                            aria-expanded={isDivOpen}
                          >
                            <div className="rwr-row-left">
                              <span className="rwr-level-badge rwr-badge-division">Division</span>
                              <span className="rwr-row-name">{divisionName}</span>
                            </div>

                            <div className="rwr-row-right">
                              <span className="rwr-count-pill">
                                <i className="mdi mdi-map-marker-multiple" /> {rangeCount} Ranges
                              </span>
                              <i
                                className={`mdi mdi-chevron-down rwr-chevron ${isDivOpen ? "rotated" : ""
                                  }`}
                              />
                            </div>

                            <div className="rwr-row-mid">
                              <StatsRow
                                stats={divisionStats}
                                allLoanTypes={allLoanTypes}
                                showVfcCount
                                jfmcLabel={jfmcLabel}
                              />
                            </div>
                          </div>

                          {/* Range Level */}
                          {isDivOpen && (
                            <div className="rwr-children rwr-division-children">
                              {divRanges
                                .filter(r =>
                                  (r.totalVFCs > 0) ||
                                  (r.vfcYearWiseReport && r.vfcYearWiseReport.length > 0)
                                )
                                .map((rangeData) => {
                                  const rangeKey = `${circleName}__${divisionName}__${rangeData.range}`;
                                  const isRangeOpen = !!expandedRanges[rangeKey];
                                  const vfcList = rangeData.vfcYearWiseReport || [];
                                  const vfcCount = vfcList.length;

                                  const rangeStats = {
                                    totalVFCs: rangeData.totalVFCs || 0,
                                    totalMembers: rangeData.totalMembers || 0,
                                    totalLoans: rangeData.totalLoans || 0,
                                    loanTypeTotals: rangeData.loanTypeTotals || {}
                                  };

                                  return (
                                    <div key={rangeData.range} className="rwr-range-block">

                                      {/* Range Header */}
                                      <div
                                        className="rwr-range-header"
                                        onClick={() => toggleRange(rangeKey)}
                                        role="button"
                                        aria-expanded={isRangeOpen}
                                      >
                                        <div className="rwr-row-left">
                                          <span className="rwr-level-badge rwr-badge-range">Range</span>
                                          <span className="rwr-row-name rwr-range-name">
                                            {rangeData.range}
                                          </span>
                                        </div>

                                        <div className="rwr-row-right">
                                          <span className="rwr-count-pill rwr-pill-green">
                                            <i className="mdi mdi-forest" /> {vfcCount} {jfmcLabel}s
                                          </span>
                                          <i
                                            className={`mdi mdi-chevron-down rwr-chevron rwr-chevron-dark ${isRangeOpen ? "rotated" : ""
                                              }`}
                                          />
                                        </div>

                                        <div className="rwr-row-mid">
                                          <StatsRow
                                            stats={rangeStats}
                                            allLoanTypes={allLoanTypes}
                                            jfmcLabel={jfmcLabel}
                                            showVfcCount
                                          />
                                        </div>
                                      </div>

                                      {/* VFC Table */}
                                      {isRangeOpen && <VfcTable vfcList={vfcList} jfmcLabel={jfmcLabel}
                                        allLoanTypes={allLoanTypes} />}
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div></div>

      {sortedReport.length === 0 && (
        <div className="rwr-empty rwr-empty-page">
          <i className="mdi mdi-table-off" style={{ fontSize: 48, color: "#A0AEC0" }} />
          <p>No report data available.</p>
        </div>
      )}
    </div>
  );
};

export default RangeWiseReportTable;
